'use client'

import { cn } from '@/lib/utils'
import { glooproNumberFormat } from '@/utils/helpers'
import * as React from 'react'
import { useEffect } from 'react'
import { NumericFormat } from 'react-number-format'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  labelClass?: string
  isRequired?: boolean
  id: string
  name: string
  value?: string
  handleBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  touched?: boolean
  error?: string
  hideErrorMsg?: boolean
  hideBorderError?: boolean
  removeArrows?: boolean
  containerClass?: string
  labelColor?: string
  errorClass?: string
  defaultValue?: string
  specialNumber?: boolean
  maxLength?: number
  maxValue?: number
  minValue?: number
  suffix?: string
  prefix?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      labelClass,
      id,
      name,
      isRequired,
      onChange,
      error,
      value,
      touched,
      handleBlur,
      containerClass,
      labelColor,
      defaultValue,
      errorClass,
      specialNumber,
      maxLength,
      maxValue,
      minValue,
      hideErrorMsg,
      hideBorderError,
      suffix,
      prefix,
      removeArrows,
      ...props
    },
    ref
  ) => {
    const timerRef = React.useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current) // clean up on unmount
      }
    }, [])

    function cleanLeadingZeros(input: string): string {
      if (input) {
        // Trim any whitespace
        const trimmed = String(input)?.trim()

        // If it's an empty string, return it
        if (trimmed === '') return ''

        // If it's a valid decimal like "0.5", "0.01", allow as-is
        if (/^0\.\d+$/.test(trimmed)) return trimmed

        // If it's all zeros like "0000", "00", reduce to single "0"
        if (/^0+$/.test(trimmed)) return '0'

        // If it's like "01", "0004", remove leading zeros
        return trimmed.replace(/^0+/, '')
      } else {
        return ''
      }
    }

    if (type === 'number-mance') {
      // const isAllowed = (values: any) => {
      //   const { floatValue, formattedValue } = values

      //   if (!formattedValue || formattedValue.trim() === '') return true

      //   const cleanedInput = formattedValue.replace(/,/g, '').split('.')[0]
      //   const inputLength = cleanedInput.length

      //   const minDigits =
      //     typeof minValue === 'number' ? String(Math.floor(minValue)).length : 0

      //   if (typeof floatValue !== 'number') return true

      //   // Delayed enforcement of minValue
      //   if (
      //     typeof minValue === 'number' &&
      //     inputLength >= minDigits &&
      //     floatValue < minValue
      //   ) {
      //     if (timerRef.current) clearTimeout(timerRef.current)

      //     timerRef.current = setTimeout(() => {
      //       const inputEl = document.getElementById(
      //         id as any
      //       ) as HTMLInputElement | null
      //       if (!inputEl) return

      //       const numeric = Number(inputEl.value.replace(/,/g, ''))
      //       if (numeric < minValue) {
      //         // Clear the input
      //         inputEl.value = ''

      //         // Trigger external onChange with empty value
      //         onChange?.({
      //           target: {
      //             id,
      //             value: '',
      //           },
      //         } as any)
      //       }
      //     }, 1000)

      //     return true // allow temporarily
      //   }

      //   // Enforce maxValue immediately
      //   if (typeof maxValue === 'number' && floatValue > maxValue) {
      //     return false
      //   }

      //   return true
      // }

      return (
        <div className={cn('mt-1', containerClass)}>
          {label && (
            <label
              htmlFor={label}
              className={cn(
                `block relative text-xs truncate SFProMedium ${
                  labelColor ? labelColor : 'text-[#0000]'
                } font-normal pb-1.5`,
                labelClass
              )}
            >
              {' '}
              {label}
              {isRequired && (
                <span className='inline-block text-red-400 text-lg pl-1 absolute bottom-1'>
                  *
                </span>
              )}{' '}
            </label>
          )}
          <NumericFormat
            {...props}
            type={'text'}
            onChange={(e: any) => {
              const obj = {
                ...e,
                target: {
                  ...e?.target,
                  value: glooproNumberFormat(e.target.value, {
                    convertToNumber: true,
                  }),
                },
              }
              onChange && onChange(obj)
            }}
            id={label ? label : id}
            name={name}
            value={cleanLeadingZeros(value as string)}
            autoComplete='off'
            className={cn(
              'flex h-10 w-full rounded-[1rem] border !shadow-none px-3 py-1 text-sm transition-colors focus-visible:ring-primary placeholder:font-light placeholder:text-sm placeholder:text-gray-300 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 border-[#E5E9EB] bg-gray-50',
              className,
              error &&
                touched &&
                !hideBorderError &&
                'border-red-500 focus-visible:ring-transparent focus-visible:ring-0'
            )}
            // ref={ref}

            defaultValue={
              type === 'color' ? 'var(--primary-color)' : defaultValue
            }
            // thousandsGroupStyle="lakh"
            thousandSeparator=','
            maxLength={maxLength}
            // max={maxValue ? Number(maxValue) : 10 }
            // size={100}
            allowNegative={false}
            decimalScale={10}
            isAllowed={(values) => {
              const { floatValue, formattedValue } = values
              if (
                typeof maxValue === 'number' &&
                floatValue !== undefined &&
                floatValue > maxValue
              ) {
                return false
              }
              return true
            }}
            // isAllowed={isAllowed}
            prefix={prefix}
            suffix={suffix}
            onBlur={(e: any) => {
              const inputValue = e?.target?.value?.replace(/,/g, '')
              const numeric = Number(inputValue)

              if (
                typeof minValue === 'number' &&
                !isNaN(numeric) &&
                numeric < minValue
              ) {
                // Clear the field if below minValue
                const inputEl = document.getElementById(
                  id
                ) as HTMLInputElement | null
                if (inputEl) {
                  inputEl.value = ''
                }

                onChange?.({
                  target: {
                    id,
                    value: '',
                  },
                } as any)
              }
            }}
          />
          {/* <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
          action=""
          style={{ width: "100%" }}
        >
         
        </form> */}
          <span
            className={cn(
              'text-xs text-red-500 hidden',
              error && 'block',
              errorClass
            )}
          >
            {error && touched && error}
          </span>
        </div>
      )
    }

    return (
      <div className={cn('mt-1', containerClass)}>
        {label && (
          <label
            htmlFor={label}
            className={cn(
              `block relative text-xs md:text-sm lg:text-base truncate DM-Sans ${
                labelColor ? labelColor : 'text-[#131515]'
              } font-[400] pb-1.5`,
              labelClass
            )}
          >
            {label}
            {isRequired && (
              <span className='inline-block text-red-400 text-sm pl-1'>*</span>
            )}{' '}
          </label>
        )}
        <input
          type={type}
          onBlur={handleBlur}
          onChange={onChange}
          id={label ? label : id}
          name={name}
          value={value}
          autoComplete='off'
          className={cn(
            'flex h-10 w-full rounded-[6.71px] border !shadow-none px-3 py-1 text-sm transition-colors focus-visible:ring-primary placeholder:font-light placeholder:text-sm placeholder:text-[#B9BCBC] file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 border-[#E5E9EB] bg-gray-50',
            removeArrows &&
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
            className,
            error &&
              touched &&
              !hideBorderError &&
              'border-red-500 focus-visible:ring-transparent focus-visible:ring-0'
          )}
          ref={ref}
          {...props}
          defaultValue={
            type === 'color' ? 'var(--primary-color)' : defaultValue
          }
          onWheel={(e) => {
            if (type === 'number') {
              e.currentTarget.blur()
            }
          }}
        />
        {/* <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
          action=""
          style={{ width: "100%" }}
        >
         
        </form> */}
        <span
          className={cn(
            'text-xs text-red-500 hidden',
            error && !hideErrorMsg && 'block',
            errorClass
          )}
        >
          {error && touched && error}
        </span>
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
